import { buf2hex } from "./util";

class SFDPNotFoundError extends Error {
    constructor(message) {
        super(message);
        this.name = "SFDPNotFoundError";
    }
}

// SFDP Signature as 32-bit little-endian: "SFDP" = 0x50444653
const SFDP_SIGNATURE = 0x50444653;

// Known Parameter IDs
const PARAMETER_IDS = {
    0xFF00: "Basic Flash Parameter Table (BFPT)",
    0xFF81: "Sector Map Parameter Table",
    0xFF03: "RPMC (Replay Protected Monotonic Counters)",
    0xFF84: "4-byte Address Instruction Table",
    0xFF04: "xSPI Profile 1.0 Table",
    0xFF05: "xSPI Profile 2.0 Table",
    0xFF06: "Status, Control and Configuration Register Map",
    0xFF07: "Command Sequences to Change to Octal DDR",
    0xFF87: "Command Sequences to Change to 8D-8D-8D (Octal DDR)",
};

const FOUR_BYTE_ADDRESS_TABLE_ID = 0xFF84;

// BFPT DWORD 16 bits 31:24 - methods to enter 4-byte addressing
const ENTER_4BYTE = {
    EN4B: 1 << 0,               // B7h
    WREN_EN4B: 1 << 1,          // 06h, then B7h
    EXTENDED_ADDRESS_REG: 1 << 2,
    BANK_REG: 1 << 3,
    NV_CONFIG_REG: 1 << 4,
    DEDICATED_OPCODES: 1 << 5,  // 13h, 12h, 21h, ...
    ALWAYS_4BYTE: 1 << 6,
};

// BFPT DWORD 16 bits 23:14 - methods to exit 4-byte addressing
const EXIT_4BYTE = {
    EX4B: 1 << 0,               // E9h
    WREN_EX4B: 1 << 1,          // 06h, then E9h
    EXTENDED_ADDRESS_REG: 1 << 2,
    BANK_REG: 1 << 3,
    HARDWARE_RESET: 1 << 4,
    SOFTWARE_RESET: 1 << 5,
    POWER_CYCLE: 1 << 6,
};

/**
 * Represents a single SFDP Parameter Header
 */
class ParameterHeader {
    constructor(data, index) {
        const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
        
        // Parameter ID: LSB at byte 0, MSB at byte 7
        const paramIdLsb = data[0];
        const paramIdMsb = data[7];
        this.parameterId = (paramIdMsb << 8) | paramIdLsb;
        
        this.minorRevision = data[1];
        this.majorRevision = data[2];
        this.lengthDwords = data[3];
        this.lengthBytes = this.lengthDwords * 4;
        
        // Table pointer is 24-bit little-endian at bytes 4-6
        this.tablePointer = data[4] | (data[5] << 8) | (data[6] << 16);
        
        this.index = index;
    }
    
    get name() {
        return PARAMETER_IDS[this.parameterId] || `Unknown (0x${this.parameterId.toString(16).toUpperCase().padStart(4, '0')})`;
    }
    
    get isBFPT() {
        return this.parameterId === 0xFF00;
    }
}

/**
 * Represents an erase type from the BFPT
 */
class EraseType {
    constructor(typeNumber, sizeExponent, opcode) {
        this.typeNumber = typeNumber;
        this.sizeExponent = sizeExponent;
        this.opcode = opcode;
        this.size = sizeExponent > 0 ? (1 << sizeExponent) : 0;
    }
    
    get isValid() {
        return this.sizeExponent > 0 && this.opcode !== 0xFF;
    }
    
    get sizeHuman() {
        if (this.size >= 1024 * 1024) {
            return `${this.size / (1024 * 1024)} MB`;
        } else if (this.size >= 1024) {
            return `${this.size / 1024} KB`;
        }
        return `${this.size} B`;
    }
    
    get opcodeHex() {
        return `0x${this.opcode.toString(16).toUpperCase().padStart(2, '0')}`;
    }
}

/**
 * Basic Flash Parameter Table (BFPT) parser
 */
class BFPT {
    constructor(data) {
        this.raw = data;
        this.eraseTypes = [];
        this._parse();
    }
    
    _parse() {
        if (this.raw.length < 36) {
            console.warn("BFPT data too short, minimum 9 DWORDs required");
            return;
        }
        
        const view = new DataView(this.raw.buffer, this.raw.byteOffset, this.raw.byteLength);
        
        // DWORD 1 (bytes 0-3)
        const dword1 = view.getUint32(0, true);
        
        // Bits 0-1: Block/Sector Erase sizes
        this.blockSectorEraseSizes = dword1 & 0x03;
        
        // Bit 2: Write Granularity (0 = 1 byte, 1 = 64 bytes or larger)
        this.writeGranularity = (dword1 >> 2) & 0x01;
        
        // Bit 3: Volatile Status Register Block Protect bits
        this.volatileStatusBlockProtect = (dword1 >> 3) & 0x01;
        
        // Bit 4: Write Enable Instruction Select (0 = 0x50, 1 = 0x06)
        this.writeEnableInstructionSelect = (dword1 >> 4) & 0x01;
        
        // Bits 8-15: 4KB Erase Opcode
        this.erase4kOpcode = (dword1 >> 8) & 0xFF;
        
        // Bit 16: 1-1-2 Fast Read support
        this.support112FastRead = Boolean(dword1 & (1 << 16));
        
        // Bits 17-18: Address Bytes
        this.addressBytesRaw = (dword1 >> 17) & 0x03;
        
        // Bit 19: DTR Clocking supported
        this.supportDTR = Boolean(dword1 & (1 << 19));
        
        // Bit 20: 1-2-2 Fast Read support
        this.support122FastRead = Boolean(dword1 & (1 << 20));
        
        // Bit 21: 1-4-4 Fast Read support
        this.support144FastRead = Boolean(dword1 & (1 << 21));
        
        // Bit 22: 1-1-4 Fast Read support
        this.support114FastRead = Boolean(dword1 & (1 << 22));
        
        // Determine address bytes (JESD216). Note this is the power-on
        // addressing mode, NOT the width the array needs: most 256 Mbit
        // parts report 0b01 and some older ones even 0b00. Use
        // Flashdumper.getAddressBytes() for the width to actually use.
        this.supports4ByteAddress = false;
        switch (this.addressBytesRaw) {
            case 0b00:
                this.addressBytes = 3; // 3-byte only
                break;
            case 0b01:
                this.addressBytes = 3; // 3- or 4-byte, defaults to 3-byte
                this.supports4ByteAddress = true;
                break;
            case 0b10:
                this.addressBytes = 4; // 4-byte only
                this.supports4ByteAddress = true;
                break;
            default:
                console.warn("BFPT address bytes field uses reserved value 0b11");
                this.addressBytes = 3;
        }
        
        // DWORD 2 (bytes 4-7) - Flash Memory Density
        const dword2 = view.getUint32(4, true);
        if (dword2 & 0x80000000) {
            // Bit 31 set: density is 2^N bits where N is bits 0-30
            this.densityBits = 1n << BigInt(dword2 & 0x7FFFFFFF);
        } else {
            // Bit 31 clear: density is (N+1) bits
            this.densityBits = BigInt(dword2 + 1);
        }
        this.densityBytes = Number(this.densityBits / 8n);
        
        // DWORD 3-7: Fast Read parameters (we'll skip detailed parsing for now)
        
        // DWORD 8 (bytes 28-31) - Erase Type 1 & 2
        if (this.raw.length >= 32) {
            const dword8 = view.getUint32(28, true);
            
            // Erase Type 1: bits 0-7 = size, bits 8-15 = opcode
            const et1Size = dword8 & 0xFF;
            const et1Opcode = (dword8 >> 8) & 0xFF;
            this.eraseTypes.push(new EraseType(1, et1Size, et1Opcode));
            
            // Erase Type 2: bits 16-23 = size, bits 24-31 = opcode
            const et2Size = (dword8 >> 16) & 0xFF;
            const et2Opcode = (dword8 >> 24) & 0xFF;
            this.eraseTypes.push(new EraseType(2, et2Size, et2Opcode));
        }
        
        // DWORD 9 (bytes 32-35) - Erase Type 3 & 4
        if (this.raw.length >= 36) {
            const dword9 = view.getUint32(32, true);
            
            // Erase Type 3: bits 0-7 = size, bits 8-15 = opcode
            const et3Size = dword9 & 0xFF;
            const et3Opcode = (dword9 >> 8) & 0xFF;
            this.eraseTypes.push(new EraseType(3, et3Size, et3Opcode));
            
            // Erase Type 4: bits 16-23 = size, bits 24-31 = opcode
            const et4Size = (dword9 >> 16) & 0xFF;
            const et4Opcode = (dword9 >> 24) & 0xFF;
            this.eraseTypes.push(new EraseType(4, et4Size, et4Opcode));
        }
        
        // Filter to only valid erase types
        this.validEraseTypes = this.eraseTypes.filter(et => et.isValid);
        
        // DWORD 10+ parsing for newer SFDP revisions
        if (this.raw.length >= 40) {
            const dword10 = view.getUint32(36, true);
            // Erase type timing information (optional)
        }
        
        // DWORD 11 (bytes 40-43) - more timing and program info
        if (this.raw.length >= 44) {
            const dword11 = view.getUint32(40, true);
            this.pageSize = 1 << ((dword11 >> 4) & 0x0F); // Page size in bytes
        } else {
            this.pageSize = 256; // Default page size
        }
        
        // Parse DWORD 15-16 for Quad Enable and other features if available
        if (this.raw.length >= 60) {
            const dword15 = view.getUint32(56, true);
            this.quadEnableRequirements = (dword15 >> 20) & 0x07;
            this.holdResetDisable = Boolean(dword15 & (1 << 23));
        }

        // DWORD 16 (bytes 60-63) - how to enter/exit 4-byte addressing.
        // All-zero or all-one fields carry no usable information.
        this.enter4ByteMethods = 0;
        this.exit4ByteMethods = 0;
        if (this.raw.length >= 64) {
            const dword16 = view.getUint32(60, true);
            const enter = (dword16 >>> 24) & 0xFF;
            const exit = (dword16 >>> 14) & 0x3FF;
            if (enter !== 0xFF) this.enter4ByteMethods = enter;
            if (exit !== 0x3FF) this.exit4ByteMethods = exit;
        }
    }
    
    get densityHuman() {
        const bytes = this.densityBytes;
        if (bytes >= 1024 * 1024 * 1024) {
            return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
        } else if (bytes >= 1024 * 1024) {
            return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
        } else if (bytes >= 1024) {
            return `${(bytes / 1024).toFixed(1)} KB`;
        }
        return `${bytes} bytes`;
    }
}

/**
 * 4-byte Address Instruction Table (JESD216B+, parameter ID 0xFF84).
 * Lists which dedicated 4-byte opcodes the flash supports.
 */
class FourByteAddressTable {
    constructor(data) {
        this.raw = data;
        const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
        const dword1 = data.length >= 4 ? view.getUint32(0, true) : 0;
        const dword2 = data.length >= 8 ? view.getUint32(4, true) : 0;

        this.supportsRead = Boolean(dword1 & (1 << 0));         // 13h
        this.supportsFastRead = Boolean(dword1 & (1 << 1));     // 0Ch
        this.supportsPageProgram = Boolean(dword1 & (1 << 6));  // 12h

        // Bits 9-12: erase type 1-4 supported; DWORD 2 holds the opcodes
        this.eraseOpcodes = {};
        for (let i = 0; i < 4; i++) {
            if (dword1 & (1 << (9 + i))) {
                this.eraseOpcodes[i + 1] = (dword2 >>> (8 * i)) & 0xFF;
            }
        }
    }

    /**
     * @param {number} typeNumber - BFPT erase type (1-4)
     * @returns {number|null} 4-byte opcode for that erase type
     */
    getEraseOpcode(typeNumber) {
        return this.eraseOpcodes[typeNumber] ?? null;
    }
}

/**
 * Main SFDP Parser class
 */
class SFDP {
    constructor(data, options = {}) {
        if (!(data instanceof Uint8Array)) {
            throw new Error("Input must be a Uint8Array");
        }
        
        this.debug = options.debug ?? false;
        
        if (this.debug) {
            console.log("Raw SFDP data:");
            console.log(buf2hex(data));
        }
        
        this.data = data;
        this.parameterHeaders = [];
        this.parameterTables = new Map();
        this.bfpt = null;
        this.fourByteAddressTable = null;
        
        this.parseHeader();
        this.parseParameterHeaders();
        this.parseParameterTables();
        
        // Setup backward compatibility aliases
        this._setupCompatibilityAliases();
    }

    parseHeader() {
        if (this.data.length < 8) {
            throw new SFDPNotFoundError("Data too short for SFDP header");
        }
        
        const dataView = new DataView(this.data.buffer, this.data.byteOffset, this.data.byteLength);

        // Parse the SFDP header (8 bytes)
        this.signature = dataView.getUint32(0, true);
        
        if (this.signature !== SFDP_SIGNATURE) {
            throw new SFDPNotFoundError(
                `Invalid SFDP signature: 0x${this.signature.toString(16).toUpperCase()} ` +
                `(expected 0x${SFDP_SIGNATURE.toString(16).toUpperCase()})`
            );
        }

        this.minorRevision = dataView.getUint8(4);
        this.majorRevision = dataView.getUint8(5);
        this.nph = dataView.getUint8(6);  // Number of Parameter Headers (actual count = NPH + 1)
        this.accessProtocol = dataView.getUint8(7);
        
        if (this.debug) {
            console.log(`SFDP Revision: ${this.majorRevision}.${this.minorRevision}`);
            console.log(`Number of Parameter Headers: ${this.nph + 1}`);
            console.log(`Access Protocol: 0x${this.accessProtocol.toString(16).toUpperCase()}`);
        }
    }
    
    parseParameterHeaders() {
        const numHeaders = this.nph + 1;
        const headerOffset = 8; // Parameter headers start at byte 8
        
        for (let i = 0; i < numHeaders; i++) {
            const offset = headerOffset + (i * 8);
            
            if (offset + 8 > this.data.length) {
                console.warn(`Truncated data at parameter header ${i}`);
                break;
            }
            
            const headerData = this.data.subarray(offset, offset + 8);
            const header = new ParameterHeader(headerData, i);
            this.parameterHeaders.push(header);
            
            if (this.debug) {
                console.log(`Parameter Header ${i}: ${header.name}`);
                console.log(`  Revision: ${header.majorRevision}.${header.minorRevision}`);
                console.log(`  Length: ${header.lengthDwords} DWORDs (${header.lengthBytes} bytes)`);
                console.log(`  Table Pointer: 0x${header.tablePointer.toString(16).toUpperCase()}`);
            }
        }
    }
    
    parseParameterTables() {
        for (const header of this.parameterHeaders) {
            const tableStart = header.tablePointer;
            const tableLength = header.lengthBytes;
            
            if (tableStart + tableLength <= this.data.length) {
                const tableData = this.data.subarray(tableStart, tableStart + tableLength);
                this.parameterTables.set(header.parameterId, tableData);
                
                // Parse BFPT specifically
                if (header.isBFPT) {
                    this.bfpt = new BFPT(tableData);
                    
                    if (this.debug) {
                        console.log("BFPT parsed:");
                        console.log(`  Density: ${this.bfpt.densityHuman}`);
                        console.log(`  Address Bytes: ${this.bfpt.addressBytes}`);
                        console.log(`  Page Size: ${this.bfpt.pageSize} bytes`);
                        console.log(`  Valid Erase Types: ${this.bfpt.validEraseTypes.length}`);
                        for (const et of this.bfpt.validEraseTypes) {
                            console.log(`    Type ${et.typeNumber}: ${et.sizeHuman} (${et.opcodeHex})`);
                        }
                    }
                } else if (header.parameterId === FOUR_BYTE_ADDRESS_TABLE_ID) {
                    this.fourByteAddressTable = new FourByteAddressTable(tableData);
                }
            } else {
                console.warn(`Parameter table 0x${header.parameterId.toString(16)} extends beyond data`);
            }
        }
    }
    
    /**
     * Number of SFDP bytes needed to cover all parameter tables. Can be
     * larger than the initial read, in which case the caller should
     * re-read SFDP with this length.
     */
    get requiredLength() {
        let end = 8 + this.parameterHeaders.length * 8;
        for (const header of this.parameterHeaders) {
            end = Math.max(end, header.tablePointer + header.lengthBytes);
        }
        return end;
    }

    /**
     * Setup backward compatibility aliases for existing code
     */
    _setupCompatibilityAliases() {
        // Get the first parameter header (BFPT) for legacy fields
        const bfptHeader = this.parameterHeaders.find(h => h.isBFPT);
        
        if (bfptHeader) {
            // Legacy parameter header fields (from first BFPT header)
            this.parameterMinorRevision = bfptHeader.minorRevision;
            this.parameterMajorRevision = bfptHeader.majorRevision;
            this.parameterLength = bfptHeader.lengthBytes;
            this.ptp = bfptHeader.tablePointer;
        }
        
        if (this.bfpt) {
            // Legacy BFPT fields
            this.addressBytes = this.bfpt.addressBytes;
            this.addressBytesRaw = this.bfpt.addressBytesRaw;
            this.bsEraseSizes = this.bfpt.blockSectorEraseSizes;
            this.writeGranularity = this.bfpt.writeGranularity;
            this.writeEnableInstructionRequired = this.bfpt.volatileStatusBlockProtect;
            this.writeEnableOpcode = this.bfpt.writeEnableInstructionSelect;
            this.eraseOpcode4k = this.bfpt.erase4kOpcode;
            this.density = this.bfpt.densityBits;
            this.densityBytes = this.bfpt.densityBytes;
            this.densityHuman = this.bfpt.densityHuman;
            this.pageSize = this.bfpt.pageSize;
            
            // Erase types for convenience
            this.eraseTypes = this.bfpt.validEraseTypes;
        }
        
        // Legacy vendor ID - this was incorrectly parsed before
        // The SFDP spec doesn't have a vendor ID field in the main header
        // Some vendors put their JEDEC ID in the parameter ID MSB
        // We'll try to extract it from parameter headers if possible
        this.vendorId = 0;
        for (const header of this.parameterHeaders) {
            // Non-JEDEC parameter headers (ID MSB != 0xFF) indicate vendor-specific
            const msb = (header.parameterId >> 8) & 0xFF;
            if (msb !== 0xFF && msb !== 0x00) {
                this.vendorId = msb;
                break;
            }
        }
        
        // Also set a placeholder jedecId for backward compatibility
        // This should really come from RDID command, not SFDP
        this.jedecId = this.vendorId;
    }
    
    /**
     * Get a parameter table by ID
     * @param {number} parameterId - The parameter ID (e.g., 0xFF00 for BFPT)
     * @returns {Uint8Array|undefined} The raw table data
     */
    getParameterTable(parameterId) {
        return this.parameterTables.get(parameterId);
    }
    
    /**
     * Check if a specific parameter table exists
     * @param {number} parameterId - The parameter ID
     * @returns {boolean}
     */
    hasParameterTable(parameterId) {
        return this.parameterTables.has(parameterId);
    }
    
    /**
     * Get the best erase command for a given size requirement
     * @param {number} minSize - Minimum erase size needed
     * @returns {EraseType|null} Best matching erase type
     */
    getBestEraseType(minSize = 0) {
        if (!this.bfpt || !this.bfpt.validEraseTypes.length) {
            return null;
        }
        
        // Sort by size ascending, find smallest that meets minSize
        const sorted = [...this.bfpt.validEraseTypes].sort((a, b) => a.size - b.size);
        
        for (const et of sorted) {
            if (et.size >= minSize) {
                return et;
            }
        }
        
        // Return largest if none meet minSize
        return sorted[sorted.length - 1];
    }
    
    /**
     * Get the 4KB erase type if available
     * @returns {EraseType|null}
     */
    get4KBEraseType() {
        if (!this.bfpt) return null;
        return this.bfpt.validEraseTypes.find(et => et.size === 4096) || null;
    }
    
    /**
     * Get the largest erase type (usually 64KB block erase)
     * @returns {EraseType|null}
     */
    getLargestEraseType() {
        if (!this.bfpt || !this.bfpt.validEraseTypes.length) return null;
        return this.bfpt.validEraseTypes.reduce((max, et) => 
            et.size > max.size ? et : max
        );
    }
    
    /**
     * Check if the flash supports 4-byte addressing
     * @returns {boolean}
     */
    supports4ByteAddressing() {
        return this.bfpt?.supports4ByteAddress ?? false;
    }
    
    /**
     * Check if a specific feature table exists
     */
    hasOctalDDRSupport() {
        return this.hasParameterTable(0xFF87) || this.hasParameterTable(0xFF07);
    }
    
    hasXSPIProfile() {
        return this.hasParameterTable(0xFF04) || this.hasParameterTable(0xFF05);
    }
    
    /**
     * Get a summary object for display
     */
    getSummary() {
        return {
            revision: `${this.majorRevision}.${this.minorRevision}`,
            density: this.bfpt?.densityHuman ?? 'Unknown',
            densityBytes: this.bfpt?.densityBytes ?? 0,
            addressBytes: this.bfpt?.addressBytes ?? 3,
            pageSize: this.bfpt?.pageSize ?? 256,
            eraseTypes: this.bfpt?.validEraseTypes.map(et => ({
                size: et.size,
                sizeHuman: et.sizeHuman,
                opcode: et.opcode,
                opcodeHex: et.opcodeHex
            })) ?? [],
            features: {
                dtr: this.bfpt?.supportDTR ?? false,
                fastRead112: this.bfpt?.support112FastRead ?? false,
                fastRead122: this.bfpt?.support122FastRead ?? false,
                fastRead114: this.bfpt?.support114FastRead ?? false,
                fastRead144: this.bfpt?.support144FastRead ?? false,
                octalDDR: this.hasOctalDDRSupport(),
                xspi: this.hasXSPIProfile(),
                fourByteAddress: this.supports4ByteAddressing()
            },
            parameterTables: this.parameterHeaders.map(h => ({
                id: h.parameterId,
                name: h.name,
                revision: `${h.majorRevision}.${h.minorRevision}`,
                size: h.lengthBytes
            }))
        };
    }
}

export {
    SFDP, SFDPNotFoundError, ParameterHeader, BFPT, EraseType, FourByteAddressTable,
    PARAMETER_IDS, ENTER_4BYTE, EXIT_4BYTE,
};
